import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Loader2, Pencil, Upload } from "lucide-react";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import { Button } from "@/components/ui/button";
import { SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/clientUtils";
import { queryClient, trpc } from "@/lib/trpcClient";
import { env } from "../../../../../../env";

export const Route = createFileRoute(
  "/_authed/_org/transactions/reimbursements/create",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);

  const uploadReceipt = async (file: File) => {
    setIsUploadingReceipt(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        `${env.VITE_SERVER_BASE_URL}/api/file/receipt-process-reimbursement`,
        {
          body: formData,
          credentials: "include",
          method: "POST",
        },
      );

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const data = await response.json();

      // Navigate to the created reimbursement
      navigate({
        params: {
          id: data.reimbursement.id,
        },
        to: "/transactions/reimbursements/$id",
      });

      toast.success("Reimbursement created successfully!");
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Upload failed";
      toast.error(`Failed to process receipt: ${errorMessage}`);
    } finally {
      setIsUploadingReceipt(false);
    }
  };

  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      uploadReceipt(acceptedFiles[0]);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".jpg", ".jpeg", ".png", ".gif", ".webp"],
    },
    maxSize: 4 * 1024 * 1024, // 4MB
    multiple: false,
    onDrop,
  });

  const { mutate: createBlank, isPending } = useMutation(
    trpc.reimbursement.createBlank.mutationOptions({
      onError(error) {
        toast.error(
          `Failed to create reimbursement: ${
            error instanceof Error ? error.message : "Unknown error"
          }`,
        );
      },
      onSuccess(data) {
        queryClient.invalidateQueries(trpc.reimbursement.list.queryOptions());

        navigate({
          params: {
            id: data.id,
          },
          to: "/transactions/reimbursements/$id",
        });
      },
    }),
  );

  return (
    <div className="flex h-full flex-col">
      <SheetHeader className="border-b px-6 py-6">
        <SheetTitle>Request Reimbursement</SheetTitle>
      </SheetHeader>

      <div className="flex grow flex-col items-center justify-center p-4">
        <div
          className={twMerge(
            isUploadingReceipt && "opacity-50",
            "flex w-3xl flex-col items-center justify-center rounded-xl border border-border p-8",
          )}
        >
          <Pencil className="mb-2 size-8 text-neutral-400" />
          <h1 className="font-medium text-2xl text-neutral-800">
            Create a Reimbursement
          </h1>

          <Button
            className="mt-4 px-8"
            disabled={isUploadingReceipt || isPending}
            onClick={() => createBlank()}
          >
            Enter Details Manually{" "}
            {isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <ArrowRight className="size-3.5" />
            )}
          </Button>
        </div>

        <div className="my-12 flex w-full max-w-96 items-center gap-5 font-medium text-neutral-500">
          <div className="h-px grow items-center bg-border" />
          OR
          <div className="h-px grow bg-border" />
        </div>

        <button
          {...getRootProps()}
          type="button"
          disabled={isUploadingReceipt}
          className={cn(
            "flex h-48 w-3xl flex-col items-center justify-center rounded-lg border-2 border-dashed bg-white p-8",
            isDragActive
              ? "border-primary bg-primary/5"
              : "border-muted-foreground/20",
            !isUploadingReceipt
              ? "cursor-auto"
              : "cursor-pointer hover:border hover:border-solid hover:bg-neutral-50",
          )}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center space-y-4">
            {isUploadingReceipt ? (
              <Loader2 className="h-8 w-8 animate-spin" />
            ) : (
              <Upload className="h-8 w-8" />
            )}
            <div className="text-center">
              <p className="text-muted-foreground text-sm">
                {isUploadingReceipt
                  ? "Processing Receipt"
                  : "Drop receipt here or click to upload"}
              </p>
              <p className="mt-1 text-muted-foreground text-xs">
                Supports JPG, PNG, PDF up to 4MB
              </p>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}
